import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ArrowLeft, Save, Package, Image, Upload } from "lucide-react";
import { inventoryService } from "@/services/inventoryService";
import financeService from "@/services/financeService";
import Button from "@/components/ui/Button";
import { toast } from "react-hot-toast";

const InventoryItemForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    current_stock: 0,
    reorder_quantity: 0,
    unit_cost: "",
    selling_price: "",
    currency: "NGN",
    location_id: "",
    supplier_name: "",
    supplier_contact: "",
    barcode: "",
    image_url: "",
    subsidiary_id: "", // Add subsidiary field
    // Additional options
    sku: "",
    unit_of_measure: "pcs",
    minimum_stock: 0,
    maximum_stock: "",
    is_active: true,
  });

  const [imageMode, setImageMode] = useState("url"); // 'url' or 'upload'
  const [imagePreview, setImagePreview] = useState("");
  const [errors, setErrors] = useState({});

  // Fetch item data for editing
  const { data: itemData, isLoading: itemLoading } = useQuery(
    ["inventory-item", id],
    () => inventoryService.getItemById(id),
    {
      enabled: isEdit && !!id,
      select: (response) => response?.data?.data?.item || response?.data?.item,
      onSuccess: (item) => {
        if (item) {
          setFormData({
            name: item.name || "",
            description: item.description || "",
            category: item.category || "",
            current_stock: parseFloat(item.current_stock) || 0,
            reorder_quantity: parseFloat(item.reorder_quantity) || 0,
            unit_cost: parseFloat(item.unit_cost) || "",
            selling_price: parseFloat(item.selling_price) || "",
            currency: item.currency || "NGN",
            location_id: item.location_id ? item.location_id.toString() : "",
            supplier_name: item.supplier_name || "",
            supplier_contact: item.supplier_contact || "",
            barcode: item.barcode || "",
            image_url: item.image_url || "",
            subsidiary_id: item.subsidiary_id
              ? item.subsidiary_id.toString()
              : "",
            sku: item.sku || "",
            unit_of_measure: item.unit_of_measure || "pcs",
            minimum_stock: parseFloat(item.minimum_stock) || 0,
            maximum_stock: parseFloat(item.maximum_stock) || "",
            is_active: item.is_active !== undefined ? item.is_active : true,
          });
          if (item.image_url) setImagePreview(item.image_url);
        }
      },
    },
  );

  // Fetch locations
  const { data: locations = [] } = useQuery(
    "inventory-locations",
    () => inventoryService.getLocations(),
    {
      select: (response) =>
        response?.data?.data?.locations || [
          { id: 1, name: "Main Warehouse" },
          { id: 2, name: "Office Storage" },
          { id: 3, name: "Store" },
        ],
    },
  );

  // Fetch subsidiaries
  const { data: subsidiaries = [] } = useQuery(
    "subsidiaries",
    () => financeService.getSubsidiaries(),
    {
      select: (response) =>
        response?.data?.data?.subsidiaries ||
        response?.data?.subsidiaries ||
        [],
      onError: (error) => {
        console.log("Subsidiaries fetch error:", error.message);
      },
    },
  );

  // Auto-generate SKU when name or category changes (only for new items)
  useEffect(() => {
    if (!isEdit && formData.name) {
      const prefix = formData.category
        ? formData.category.substring(0, 3).toUpperCase()
        : "ITM";
      const random = Math.random().toString(36).substring(2, 6).toUpperCase();
      const newSku = `${prefix}-${random}`;
      if (
        !formData.sku ||
        formData.sku.startsWith("ITM-") ||
        formData.sku.match(/^[A-Z]{3}-[A-Z0-9]{4}$/)
      ) {
        setFormData((prev) => ({ ...prev, sku: newSku }));
      }
    }
  }, [formData.name, formData.category, isEdit]);

  // Auto-calculate reorder quantity when maximum stock changes
  useEffect(() => {
    if (formData.maximum_stock && formData.reorder_quantity === 0) {
      const maxStock = parseFloat(formData.maximum_stock) || 0;
      const minStock = parseFloat(formData.minimum_stock) || 0;
      if (maxStock > minStock) {
        setFormData((prev) => ({
          ...prev,
          reorder_quantity: maxStock - minStock,
        }));
      }
    }
  }, [formData.maximum_stock, formData.minimum_stock]);

  const saveMutation = useMutation(
    (data) =>
      isEdit
        ? inventoryService.updateItem(id, data)
        : inventoryService.createItem(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("inventory-items");
        toast.success(`Item ${isEdit ? "updated" : "created"} successfully`);
        navigate("/inventory/items");
      },
      onError: (error) => {
        const message =
          error.response?.data?.message ||
          `Failed to ${isEdit ? "update" : "create"} item`;
        toast.error(message);
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        }
      },
    },
  );

  const categories = [
    "Electronics",
    "Furniture",
    "Stationery",
    "Equipment",
    "Supplies",
    "Tools",
    "Consumables",
    "Raw Materials",
    "Other",
  ];
  const currencies = ["NGN", "USD", "EUR", "GBP"];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Item name is required";
    if (!formData.category) newErrors.category = "Category is required";
    if (!formData.current_stock && formData.current_stock !== 0)
      newErrors.current_stock = "Quantity is required";
    if (!formData.unit_cost) newErrors.unit_cost = "Unit cost is required";
    if (!formData.selling_price)
      newErrors.selling_price = "Selling price is required";
    if (!formData.location_id) newErrors.location_id = "Location is required";
    if (!formData.subsidiary_id)
      newErrors.subsidiary_id = "Subsidiary is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    const submitData = {
      ...formData,
      sku: formData.sku || `ITM-${Date.now().toString().slice(-6)}`,
      unit_cost: parseFloat(formData.unit_cost) || 0,
      selling_price: parseFloat(formData.selling_price) || 0,
      current_stock: parseFloat(formData.current_stock) || 0,
      minimum_stock: parseFloat(formData.minimum_stock) || 0,
      maximum_stock: parseFloat(formData.maximum_stock) || null,
      reorder_point: parseFloat(formData.minimum_stock) || 0,
      reorder_quantity: parseFloat(formData.reorder_quantity) || 0,
      location_id: parseInt(formData.location_id),
      subsidiary_id: formData.subsidiary_id
        ? parseInt(formData.subsidiary_id)
        : null,
    };

    console.log("🚀 SUBMITTING INVENTORY ITEM");
    console.log("📦 Form Data:", formData);
    console.log("📤 Submit Data:", submitData);
    console.log("📊 Quantity:", submitData.current_stock);
    console.log("💵 Unit Cost:", submitData.unit_cost);
    console.log(
      "💸 Total Cost:",
      submitData.current_stock * submitData.unit_cost,
    );

    saveMutation.mutate(submitData);
  };

  if (isEdit && itemLoading) {
    return <div className="p-6 text-center">Loading item data...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate("/inventory/items")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEdit ? "Edit Item" : "Add New Item"}
            </h1>
            <p className="text-gray-600">
              {isEdit ? "Update item details" : "Create a new inventory item"}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Required Fields */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Package className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium">Item Details</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Item Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.name ? "border-red-500" : "border-gray-300"}`}
                placeholder="e.g., Dell Laptop XPS 13"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.category ? "border-red-500" : "border-gray-300"}`}
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="text-red-500 text-sm mt-1">{errors.category}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Quantity <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="current_stock"
                value={formData.current_stock}
                onChange={handleChange}
                min="0"
                className={`w-full px-3 py-2 border rounded-md ${errors.current_stock ? "border-red-500" : "border-gray-300"}`}
              />
              {errors.current_stock && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.current_stock}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Reorder Quantity <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="reorder_quantity"
                value={formData.reorder_quantity}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">
                Auto-calculated: Max Stock - Min Stock
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Unit Cost <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="unit_cost"
                value={formData.unit_cost}
                onChange={handleChange}
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 border rounded-md ${errors.unit_cost ? "border-red-500" : "border-gray-300"}`}
                placeholder="0.00"
              />
              {errors.unit_cost && (
                <p className="text-red-500 text-sm mt-1">{errors.unit_cost}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Selling Price <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="selling_price"
                value={formData.selling_price}
                onChange={handleChange}
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 border rounded-md ${errors.selling_price ? "border-red-500" : "border-gray-300"}`}
                placeholder="0.00"
              />
              {errors.selling_price && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.selling_price}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Currency <span className="text-red-500">*</span>
              </label>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {currencies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Location <span className="text-red-500">*</span>
              </label>
              <select
                name="location_id"
                value={formData.location_id}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.location_id ? "border-red-500" : "border-gray-300"}`}
              >
                <option value="">Select Location</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
              {errors.location_id && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.location_id}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Subsidiary <span className="text-red-500">*</span>
              </label>
              <select
                name="subsidiary_id"
                value={formData.subsidiary_id}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.subsidiary_id ? "border-red-500" : "border-gray-300"}`}
              >
                <option value="">Select Subsidiary</option>
                {subsidiaries.map((subsidiary) => (
                  <option key={subsidiary.id} value={subsidiary.id}>
                    {subsidiary.name}
                  </option>
                ))}
              </select>
              {errors.subsidiary_id && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.subsidiary_id}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Select the subsidiary/branch for this item
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Supplier Name
              </label>
              <input
                type="text"
                name="supplier_name"
                value={formData.supplier_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Supplier name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Supplier Contact
              </label>
              <input
                type="text"
                name="supplier_contact"
                value={formData.supplier_contact}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Phone or email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Barcode (Optional)
              </label>
              <input
                type="text"
                name="barcode"
                value={formData.barcode}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Enter barcode if available"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty if no barcode available
              </p>
            </div>

            {/* Image Field */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">
                Item Image
              </label>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setImageMode("url")}
                  className={`px-3 py-1 text-sm rounded ${imageMode === "url" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
                >
                  URL
                </button>
                <button
                  type="button"
                  onClick={() => setImageMode("upload")}
                  className={`px-3 py-1 text-sm rounded ${imageMode === "upload" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
                >
                  Upload
                </button>
              </div>
              {imageMode === "url" ? (
                <input
                  type="url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={(e) => {
                    handleChange(e);
                    setImagePreview(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="https://example.com/image.jpg"
                />
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setImagePreview(reader.result);
                          setFormData((prev) => ({
                            ...prev,
                            image_url: reader.result,
                          }));
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      Click to upload image
                    </span>
                  </label>
                </div>
              )}
              {imagePreview && (
                <div className="mt-2 relative inline-block">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="h-20 w-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setImagePreview("");
                      setFormData((prev) => ({ ...prev, image_url: "" }));
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/inventory/items")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={saveMutation.isLoading}>
            <Save className="h-4 w-4 mr-2" />
            {saveMutation.isLoading
              ? "Saving..."
              : isEdit
                ? "Update Item"
                : "Create Item"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InventoryItemForm;
